/**
 * HTTP client for the live TownDesk API. The SPA + API are same-origin behind CloudFront, so all
 * paths are RELATIVE (`/api/...`) — no host. Staff/admin calls carry the Cognito ID token as
 * `Authorization` (set via setAuthToken from the Amplify session). Errors from the Go handlers are
 * `{ error: code, message? }` (httpx.go) — surfaced as ApiError so the UI can show a message.
 */

import { beginRequest, endRequest } from "./pending.js";
import { takePendingCaptchaToken } from "./wafCaptcha.js";

/** Base path — same-origin behind CloudFront (ADR-0004). Override for tests only. */
export const API_BASE = "/api";

let authToken: string | null = null;

/** Set/clear the Cognito ID token used as Authorization on staff/admin calls. */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/** Read the current token (for raw fetches that bypass apiFetch, e.g. blob export). */
export function getAuthToken(): string | null {
  return authToken;
}

/**
 * Registered by StaffAuth's LiveAuthProvider on mount — forces a Cognito session refresh and
 * returns the fresh ID token (or null if the refresh itself fails, e.g. the session is truly
 * expired/revoked). Kept as an injected callback (not a static import of aws-amplify/auth) so this
 * client module stays framework-agnostic and mock-friendly; unset in tests / mock mode, in which
 * case a 401 just surfaces as today (no refresh attempted).
 */
let refreshAuthToken: (() => Promise<string | null>) | null = null;

/** Wire up the one-shot-refresh-on-401 hook (see apiFetch below). */
export function setAuthTokenRefresher(refresher: (() => Promise<string | null>) | null): void {
  refreshAuthToken = refresher;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly retriable = true,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Attach the Authorization header (staff/admin endpoints). */
  auth?: boolean;
  /** Query params appended to the path. */
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v != null && v !== "") params.set(k, v);
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/** Core fetch → JSON, mapping the Go error envelope to ApiError. Brackets the whole call with the
 *  global pending-request counter (begin/end in try/finally) so the app-wide top progress bar
 *  reflects every network request automatically — see api/pending.ts + components/TopProgressBar. */
export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  // Attach a freshly-solved WAF CAPTCHA token as a header when one is pending (set by
  // ensureCaptchaToken just before a gated public POST). This is the cookie-independent delivery
  // path that makes the captcha-gated forms work inside the cross-site embed iframe, where the
  // partitioned `aws-waf-token` cookie can't ride the fetch (see api/wafCaptcha.ts header comment).
  // Single-use — consumed here so it never leaks onto an unrelated later request; the standalone
  // same-origin cookie path is unaffected when there's no pending token.
  const wafToken = takePendingCaptchaToken();

  beginRequest();
  try {
    return await doFetch<T>(path, opts, wafToken, true);
  } finally {
    endRequest();
  }
}

/** The actual fetch + error mapping, split out so a 401 on an authed call can transparently
 *  refresh the Cognito session and retry ONCE (allowRetry) — the fix for the "navigate back to a
 *  staff list after the ID token expired" class of bug: without this, a request made after the
 *  token expired (e.g. returning to a list from a screen the user lingered on) fails silently and
 *  only a full page refresh (which re-bootstraps auth) recovers. A retry that still 401s throws as
 *  before. Never retries non-auth calls or when no refresher is registered (mocks/tests). */
async function doFetch<T>(
  path: string,
  opts: RequestOptions,
  wafToken: string | null,
  allowRetry: boolean,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (opts.auth) {
    if (!authToken) throw new ApiError("Not signed in.", 401, "unauthorized", false);
    headers["authorization"] = authToken;
  }
  if (wafToken) headers["x-aws-waf-token"] = wafToken;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch {
    throw new ApiError("Network error — please try again.", 0, "network", true);
  }

  const text = await res.text();
  const data = text ? safeParse(text) : undefined;

  if (!res.ok) {
    if (res.status === 401 && opts.auth && allowRetry && refreshAuthToken) {
      const fresh = await refreshAuthToken().catch(() => null);
      if (fresh) {
        setAuthToken(fresh);
        return doFetch<T>(path, opts, wafToken, false);
      }
    }
    const code = (data as { error?: string })?.error;
    const message =
      (data as { message?: string })?.message ??
      humanizeError(res.status, code) ??
      `Request failed (${res.status}).`;
    throw new ApiError(message, res.status, code, res.status >= 500 || res.status === 0);
  }
  return data as T;
}

/** PUT bytes to a presigned S3 URL (absolute; not through /api). Used by uploads. */
export async function putToPresignedUrl(
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress?: (pct: number) => void,
): Promise<void> {
  // XHR (not fetch) so we get upload progress for the attachment-tile bar.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("content-type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiError("Upload failed.", xhr.status, "upload_failed", true));
    xhr.onerror = () => reject(new ApiError("Upload failed.", 0, "upload_failed", true));
    xhr.send(file);
  });
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function humanizeError(status: number, code?: string): string | undefined {
  switch (code) {
    case "validation_error":
      return "Some fields need attention. Please review the form.";
    case "not_found":
      return "We couldn't find that.";
    case "unauthorized":
      return "Please sign in again.";
    case "forbidden":
      return "You don't have access to that.";
    case "invalid_transition":
      return "That status change isn't allowed from the current state.";
    case "not_provisioned":
      return "This account hasn't been invited yet — send a new invite instead of a resend.";
    default:
      break;
  }
  if (status === 429) return "Too many tries. Please wait a minute.";
  return undefined;
}
